/**
 * Auth Context for Super Admin Panel
 *
 * Manages authentication state across the application.
 * Prevents unnecessary re-validations and login/logout loops.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import logger from '../utils/logger';
import { useTenant } from './TenantContext';

const AuthContext = createContext({
  isAuthenticated: false,
  isLoading: true,
  adminId: null,
  login: () => {},
  logout: () => {},
  checkSession: () => {},
});

export const AuthProvider = ({ children }) => {
  const { loading: tenantLoading } = useTenant();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminId, setAdminId] = useState(null);
  const sessionChecked = useRef(false);
  const isCheckingSession = useRef(false);

  const checkSession = useCallback(async (force = false) => {
    // Prevent duplicate session checks
    if (isCheckingSession.current) {
      return isAuthenticated;
    }

    // Skip if already checked and not forced
    if (sessionChecked.current && !force) {
      return isAuthenticated;
    }

    isCheckingSession.current = true;

    try {
      const response = await api.get('/api/super-admin-session-check');
      const loggedIn = response.data.loggedIn === true;

      setIsAuthenticated(loggedIn);
      setAdminId(loggedIn ? response.data.adminId : null);
      sessionChecked.current = true;

      if (!loggedIn) {
        // Only clear token if server explicitly says not logged in
        localStorage.removeItem('superAdminToken');
      }

      return loggedIn;
    } catch (error) {
      logger.error('Session check error:', error.message);
      // Don't immediately clear auth on network errors
      // Only clear if it's a 401 response
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        setAdminId(null);
        localStorage.removeItem('superAdminToken');
      }
      sessionChecked.current = true;
      return isAuthenticated;
    } finally {
      setIsLoading(false);
      isCheckingSession.current = false;
    }
  }, [isAuthenticated]);

  const login = useCallback((token, admin) => {
    if (token) {
      localStorage.setItem('superAdminToken', token);
    }
    setIsAuthenticated(true);
    setAdminId(admin?.id || null);
    sessionChecked.current = true;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/super-admin-logout');
    } catch (error) {
      logger.error('Logout error:', error.message);
    } finally {
      localStorage.removeItem('superAdminToken');
      setIsAuthenticated(false);
      setAdminId(null);
      sessionChecked.current = false;
    }
  }, []);

  // Check session on initial load only - wait for tenant to load first
  useEffect(() => {
    if (!tenantLoading) {
      checkSession();
    }
  }, [tenantLoading]);

  const value = {
    isAuthenticated,
    isLoading,
    adminId,
    login,
    logout,
    checkSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
